import { User, LoginCredentials } from '../types';
import { STORAGE_KEYS } from '../config/constants';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { api, getErrorMessage, handleServiceError } from './api';

/** E-mail é tratado sem diferenciar maiúsculas/minúsculas — normaliza antes de enviar. */
const normEmail = (v: string): string => (v || '').trim().toLowerCase();

// Token validation utilities
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch {
    return true; // Se não conseguir decodificar, considera expirado
  }
};

const getTokenPayload = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const validateTokenUserConsistency = (token: string, user: User): boolean => {
  const payload = getTokenPayload(token);
  if (!payload || !user) return false;
  
  // Verificar se o token pertence ao usuário atual
  return payload.sub === user.email || payload.sub === user.username;
};

export const authService = {
  /**
   * Login usando OAuth2 Password Flow
   * IMPORTANTE: Backend espera form-data, não JSON!
   */
  login: async (credentials: LoginCredentials): Promise<{ access_token: string; token_type: string }> => {
    try {
      // Limpar dados antigos antes de fazer login
      authService.logout();
      
      // Criar form-data para OAuth2 Password Flow
      const formData = new URLSearchParams();
      // Backend aceita e-mail OU username aqui. Só faz trim: baixar para minúsculo quebraria
      // login por username com maiúscula — a caixa do e-mail já é resolvida no backend (lower()).
      formData.append('username', (credentials.email || '').trim());
      formData.append('password', credentials.password);

      console.log('[authService] Enviando login para /token com email:', credentials.email);

      const response = await api.post('/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      console.log('[authService] Resposta do /token:', response?.status, response?.data);

      // Resposta do backend: { "access_token": "...", "token_type": "bearer" }
      const { access_token, token_type } = response?.data || {};

      if (!access_token) {
        const errorMsg = 'Token não recebido do servidor';
        console.error('[authService]', errorMsg);
        throw new Error(errorMsg);
      }

      // Validar token antes de salvar
      if (isTokenExpired(access_token)) {
        throw new Error('Token recebido já está expirado');
      }

      // Salvar token no localStorage
      authService.setStoredToken(access_token);

      console.log('[authService] Token salvo, buscando dados do usuário');

      // Buscar dados do usuário autenticado
      const user = await authService.getCurrentUser();
      
      // Validar consistência token ↔ usuário
      if (!validateTokenUserConsistency(access_token, user)) {
        authService.logout();
        throw new Error('Inconsistência entre token e usuário detectada');
      }

      authService.setStoredUser(user);

      console.log('[authService] Login completo, usuário:', user.email, 'org:', user.organization_id);

      return {
        access_token,
        token_type,
      };
    } catch (error: any) {
      console.error('[authService] Erro na chamada login:', error);
      console.error('[authService] Erro response:', error.response?.status, error.response?.data);
      
      // Mensagem específica para 401 Unauthorized
      if (error.response?.status === 401) {
        throw new Error('Email ou senha incorretos. Verifique suas credenciais e tente novamente.');
      }
      
      const errorMsg = getErrorMessage(error);
      console.error('[authService] Mensagem extraída:', errorMsg);
      
      // Limpar dados em caso de erro
      authService.logout();
      throw new Error(errorMsg);
    }
  },

  /**
   * Busca dados do usuário autenticado
   * Usa o token armazenado automaticamente via interceptor
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await api.get(API_ENDPOINTS.USERS.ME);
      return response?.data;
    } catch (error: any) {
      handleServiceError(error, { feature: 'auth', action: 'get_current_user' });
    }
  },

  /**
   * Valida se o token atual é válido e consistente com o usuário
   */
  validateAuthState: async (): Promise<{ valid: boolean; user?: User; error?: string }> => {
    const token = authService.getStoredToken();
    const user = authService.getStoredUser();

    if (!token || !user) {
      return { valid: false, error: 'Token ou usuário não encontrados' };
    }

    // Verificar expiração
    if (isTokenExpired(token)) {
      authService.logout();
      return { valid: false, error: 'Token expirado' };
    }

    // Verificar consistência
    if (!validateTokenUserConsistency(token, user)) {
      authService.logout();
      return { valid: false, error: 'Inconsistência entre token e usuário' };
    }

    // Verificar se usuário ainda existe no backend
    try {
      const currentUser = await authService.getCurrentUser();
      
      // Verificar se organização mudou
      if (currentUser.organization_id !== user.organization_id) {
        console.log('[authService] Organização do usuário mudou, atualizando...');
        authService.setStoredUser(currentUser);
      }
      
      return { valid: true, user: currentUser };
    } catch (error) {
      authService.logout();
      return { valid: false, error: 'Usuário inválido no backend' };
    }
  },

  /**
   * Registra novo usuário
   */
  register: async (data: {
    name: string;
    email: string;
    password: string;
  }): Promise<User> => {
    try {
      const response = await api.post('/users/register', {
        username: data.name,
        email: normEmail(data.email),
        password: data.password,
        role: 'user',
        role_description: 'Usuário padrão',
        profile_by_ai: 'Aguardando geração.',
      });

      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'auth', action: 'register' });
    }
  },

  /**
   * Cadastro público com criação de organização
   * Todos os planos incluem 7 dias de teste gratuito
   */
  registerWithOrganization: async (data: {
    name: string;
    organization_name: string;
    email: string;
    phone: string;
    password: string;
    plan_type: 'starter' | 'growth' | 'enterprise';
  }): Promise<User> => {
    try {
      const response = await api.post('/users/signup', { ...data, email: normEmail(data.email) });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'auth', action: 'register_with_organization' });
    }
  },

  /**
   * Faz logout removendo token e dados do usuário
   */
  logout: () => {
    console.log('[authService] Fazendo logout - limpando localStorage');
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.theme);
    // Limpar qualquer outro dado que possa causar inconsistência
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('prainte_') || key.startsWith('auth')) {
        localStorage.removeItem(key);
      }
    });
  },

  /**
   * Verifica se usuário está autenticado
   */
  isAuthenticated: (): boolean => {
    const token = authService.getStoredToken();
    if (!token) return false;
    
    // Verificar expiração
    if (isTokenExpired(token)) {
      authService.logout();
      return false;
    }
    
    return true;
  },

  /**
   * Solicita recuperação de senha - envia email com link
   */
  requestPasswordReset: async (email: string): Promise<{ message: string }> => {
    try {
      const response = await api.post('/token/password-reset/request', { email: normEmail(email) });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'auth', action: 'request_password_reset' });
    }
  },

  /**
   * Redefine a senha usando o token recebido por email
   */
  confirmPasswordReset: async (token: string, newPassword: string): Promise<{ message: string }> => {
    try {
      const response = await api.post('/token/password-reset/confirm', {
        token,
        new_password: newPassword,
      });
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'auth', action: 'confirm_password_reset' });
    }
  },

  /**
   * Verifica se um token de reset é válido
   */
  validateResetToken: async (token: string): Promise<{ valid: boolean; email?: string }> => {
    try {
      const response = await api.get(`/token/password-reset/validate?token=${encodeURIComponent(token)}`);
      return response?.data;
    } catch (error) {
      return { valid: false };
    }
  },

  // Helpers de armazenamento local
  getStoredToken: (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.token);
  },

  setStoredToken: (token: string): void => {
    localStorage.setItem(STORAGE_KEYS.token, token);
  },

  getStoredUser: (): User | null => {
    const userStr = localStorage.getItem(STORAGE_KEYS.user);
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      localStorage.removeItem(STORAGE_KEYS.user);
      return null;
    }
  },

  setStoredUser: (user: User): void => {
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  },
};
